// The Conversation tab of a merge request (spec/dashboard.md section 12.3; spec/api-conventions.md section 12.2 "Comments"):
// the flat thread oldest first (`GET …/merge-requests/{mr}/comments`, keyset pages with Load more), each comment with its
// author, time (the display zone), body (plain text, line breaks kept) and a Resolved badge naming who and when; Edit (the
// author only, `PATCH` with `If-Match`), Resolve / Unresolve (`content_write` or the author), Delete (the author or
// `manage_settings`, confirmed); the composer below (1–4000 characters, Ctrl+Enter posts; `content_read` suffices). After
// every write the comments and the request (its `comments` counts) are re-read. The server never blocks a merge on them.
import { useRef, useState } from 'react';
import { ops } from '../../api/operations';
import type { MergeRequest, MergeRequestComment, Page } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { AlertCircle } from '../../ui/icons';
import { TextArea } from '../../ui/inputs';
import { LoadingPane } from '../../ui/PageStates';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { asApiError, mrKey, mrPrefix, PAGE_SIZE, plural } from './merge';

export const COMMENT_MAX = 4000;
/** the counter shows from here on (near the limit) */
const COUNTER_FROM = 3600;

/** The request's comment counts (an older server or a fixture may not send them). */
export function commentCounts(mr: MergeRequest): { total: number; unresolved: number } {
  const c = (mr as { comments?: { total?: number | string; unresolved?: number | string } | null }).comments;
  return { total: Number(c?.total ?? 0) || 0, unresolved: Number(c?.unresolved ?? 0) || 0 };
}

/** "3 comments · 2 unresolved" (the unresolved part only when any). */
export function commentSummary(mr: MergeRequest): string {
  const { total, unresolved } = commentCounts(mr);
  return `${plural(total, 'comment')}${unresolved > 0 ? ` · ${unresolved} unresolved` : ''}`;
}

/** Whether a body is postable: 1–4000 characters, not only white space. */
function bodyProblem(text: string): string | null {
  if (text.trim() === '') return 'Write something first.';
  if (text.length > COMMENT_MAX) return `Too long: ${text.length} of ${COMMENT_MAX} characters.`;
  return null;
}

function Counter({ text }: { text: string }) {
  if (text.length < COUNTER_FROM) return null;
  const over = text.length > COMMENT_MAX;
  return (
    <span className={`mr-comment-counter ${over ? 'over' : ''}`} data-testid="mr-comment-counter" aria-live="polite">
      {text.length} / {COMMENT_MAX}
    </span>
  );
}

export function Conversation({ mr }: { mr: MergeRequest }) {
  const { org, game, can } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const id = String(mr.number);
  const key = mrKey(org, game.slug, id, 'comments');
  const fetchPage = (cursor: string | null) =>
    client.data(ops.listMergeRequestComments, { path: { org, game: game.slug, mr: id }, query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } }) as unknown as Promise<Page<MergeRequestComment>>;
  const listQ = useQuery(key, () => fetchPage(null));
  const first = listQ.data as Page<MergeRequestComment> | undefined;

  // the pages after the first: valid only for the first page they were loaded after (a re-read drops them)
  const [more, setMore] = useState<{ base: Page<MergeRequestComment>; items: MergeRequestComment[]; cursor: string | null } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const firstRef = useRef(first);
  firstRef.current = first;
  const extra = more && more.base === first ? more : null;
  const comments: MergeRequestComment[] = [...(first?.items ?? []), ...(extra?.items ?? [])];
  const nextCursor = extra ? extra.cursor : (first?.nextCursor ?? null);

  const loadMore = async () => {
    if (!first || !nextCursor || loadingMore) return;
    const base = first;
    setLoadingMore(true);
    try {
      const page = await fetchPage(nextCursor);
      if (firstRef.current !== base) return;
      setMore({ base, items: [...(extra?.items ?? []), ...(page.items ?? [])], cursor: page.nextCursor ?? null });
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setLoadingMore(false);
    }
  };

  /** after every write: the comments and the request (its counts) */
  const reread = () => queryCache.invalidate(mrPrefix(org, game.slug));
  const error = listQ.error ? asApiError(listQ.error) : null;

  return (
    <div className="mr-conversation" data-testid="mr-conversation">
      {error ? (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">
            {error.message}
            {error.requestId ? ` (request ${error.requestId})` : ''}
          </div>
          <Button size="s" onClick={() => queryCache.invalidate(key)}>
            Retry
          </Button>
        </div>
      ) : !first ? (
        <LoadingPane rows={3} label="Loading the conversation" />
      ) : comments.length === 0 ? (
        <p className="muted" data-testid="mr-comments-empty">
          No comments yet.
        </p>
      ) : (
        <ol className="mr-comments" aria-label="Comments">
          {comments.map((c) => (
            <CommentItem key={c.id} mr={mr} comment={c} onChanged={reread} />
          ))}
        </ol>
      )}
      {nextCursor && (
        <div className="mr-more">
          <span className="muted small">{comments.length} loaded, more available</span>
          <Button size="s" data-testid="mr-comments-more" onClick={() => void loadMore()} busy={loadingMore}>
            Load more
          </Button>
        </div>
      )}
      <Composer mr={mr} onPosted={reread} disabled={!can('content_read')} />
    </div>
  );
}

function CommentItem({ mr, comment: c, onChanged }: { mr: MergeRequest; comment: MergeRequestComment; onChanged: () => void }) {
  const { org, game, can, permissions, offsets } = useGame();
  const { client, session } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [zone] = useTimeZone();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body);
  const [busy, setBusy] = useState<string | null>(null);
  const when = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  const me = permissions?.actor?.id;
  const mine = (!!me && c.author?.id === me) || (!!session?.email && c.author?.label === session.email);
  const canResolve = mine || can('content_write');
  const canDelete = mine || can('manage_settings');
  const resolved = !!c.resolvedAt;
  const path = { org, game: game.slug, mr: String(mr.number), comment: c.id };
  const edited = !!c.updatedAt && !!c.createdAt && c.updatedAt !== c.createdAt;

  const run = async (name: string, call: () => Promise<unknown>, done: string) => {
    setBusy(name);
    try {
      await call();
      toasts.success(done);
      return true;
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.isVersionConflict ? 'Someone changed this comment meanwhile; it was re-read.' : err.message, { requestId: err.requestId });
      return false;
    } finally {
      setBusy(null);
      onChanged();
    }
  };
  const save = async () => {
    if (bodyProblem(draft)) return;
    if (await run('save', () => client.data(ops.updateMergeRequestComment, { path, body: { body: draft }, ifMatch: Number(c.version) }), 'Comment saved')) setEditing(false);
  };
  const resolve = () => run('resolve', () => client.data(ops.resolveMergeRequestComment, { path }), 'Comment resolved');
  const unresolve = () => run('unresolve', () => client.data(ops.unresolveMergeRequestComment, { path }), 'Comment unresolved');
  const remove = async () => {
    if (!(await confirm({ title: 'Delete this comment?', body: 'The comment is removed for everyone; this cannot be undone.', confirmLabel: 'Delete', tone: 'danger' }))) return;
    await run('delete', () => client.data(ops.deleteMergeRequestComment, { path }), 'Comment deleted');
  };

  const problem = bodyProblem(draft);
  return (
    <li className={`mr-comment ${resolved ? 'resolved' : ''}`} data-testid={`mr-comment-${c.id}`}>
      <div className="mr-comment-head">
        <strong className="mr-comment-author">{c.author?.label ?? '—'}</strong>
        <span className="muted small" data-testid="mr-comment-time">
          {when(c.createdAt)}
          {edited ? ' · edited' : ''}
        </span>
        {resolved && (
          <span data-testid="mr-comment-resolved">
            <Badge tone="success" title={`Resolved by ${c.resolvedBy?.label ?? 'someone'} ${when(c.resolvedAt)}`}>
              Resolved
            </Badge>{' '}
            <span className="muted small">
              by {c.resolvedBy?.label ?? '—'} {when(c.resolvedAt)}
            </span>
          </span>
        )}
        <div className="spacer" />
        {!editing && (
          <div className="mr-comment-actions" role="group" aria-label="Comment actions">
            {mine && (
              <Button
                size="s"
                variant="ghost"
                onClick={() => {
                  setDraft(c.body);
                  setEditing(true);
                }}
                disabled={busy !== null}
              >
                Edit
              </Button>
            )}
            {canResolve &&
              (resolved ? (
                <Button size="s" variant="ghost" onClick={() => void unresolve()} busy={busy === 'unresolve'} disabled={busy !== null}>
                  Unresolve
                </Button>
              ) : (
                <Button size="s" variant="ghost" onClick={() => void resolve()} busy={busy === 'resolve'} disabled={busy !== null}>
                  Resolve
                </Button>
              ))}
            {canDelete && (
              <Button size="s" variant="ghost" onClick={() => void remove()} busy={busy === 'delete'} disabled={busy !== null}>
                Delete
              </Button>
            )}
          </div>
        )}
      </div>
      {editing ? (
        <div className="mr-comment-edit">
          <TextArea
            aria-label="Edit comment"
            value={draft}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                void save();
              } else if (e.key === 'Escape') setEditing(false);
            }}
          />
          <div className="mr-composer-foot">
            <Counter text={draft} />
            <div className="spacer" />
            <Button size="s" onClick={() => setEditing(false)} disabled={busy === 'save'}>
              Cancel
            </Button>
            <Button size="s" variant="primary" onClick={() => void save()} busy={busy === 'save'} disabled={problem !== null} title={problem ?? 'Save the comment'}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="mr-comment-body" data-testid="mr-comment-body">
          {c.body}
        </p>
      )}
    </li>
  );
}

function Composer({ mr, onPosted, disabled }: { mr: MergeRequest; onPosted: () => void; disabled: boolean }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const problem = bodyProblem(text);

  const post = async () => {
    if (busy || disabled || problem) return;
    setBusy(true);
    try {
      await client.data(ops.createMergeRequestComment, { path: { org, game: game.slug, mr: String(mr.number) }, body: { body: text } });
      setText('');
      toasts.success('Comment posted');
    } catch (e) {
      const err = asApiError(e);
      toasts.error(err.message, { requestId: err.requestId });
    } finally {
      setBusy(false);
      onPosted();
    }
  };

  return (
    <div className="mr-composer" data-testid="mr-composer">
      <TextArea
        aria-label="New comment"
        placeholder={disabled ? "You lack the 'content_read' permission." : 'Leave a comment (Ctrl+Enter to post)'}
        value={text}
        disabled={disabled || busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            void post();
          }
        }}
      />
      <div className="mr-composer-foot">
        <Counter text={text} />
        {text.length > COMMENT_MAX && <span className="mr-comment-counter over small">{problem}</span>}
        <div className="spacer" />
        <Button variant="primary" size="s" onClick={() => void post()} busy={busy} disabled={problem !== null} missingPermission={disabled ? 'content_read' : null} title={problem ?? 'Post the comment (Ctrl+Enter)'}>
          Comment
        </Button>
      </div>
    </div>
  );
}
